/* eslint-disable react/no-array-index-key */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import mime from 'mime-types'
import { cx } from '@emotion/css'
import ClipIcon from '@offerzen/zenicon/ClipIcon'
import Attachment from '../Attachment'
import Badge from '../Badge'
import FormErrorMessage from '../FormErrorMessage'
import styles, { labelColor } from './styles'

class MultiFileUpload extends Component {
  // eslint-disable-next-line react/state-in-constructor
  state = {
    error: [],
  }

  handleUpload = ev => {
    this.setState({ error: [] })

    Array.from(ev.target.files).forEach(file => {
      // Make sure there is a file
      if (!file) {
        this.setState({
          // eslint-disable-next-line react/no-access-state-in-setstate
          error: [...this.state.error, `Unfortunately we can't process the file ${file.name}. Please try a different file.`],
        })
        return
      }

      // Only allow files smaller than 10MB
      if (file.size >= 10485760) {
        this.setState({
          // eslint-disable-next-line react/no-access-state-in-setstate
          error: [...this.state.error, `We could not upload the file ${file.name}, as it exceeds the 10MB limit.`],
        })
        return
      }

      const reader = new FileReader()

      reader.onloadend = async output => {
        const params = {
          name: file.name,
          url: output.target.result,
        }

        // If there's no file type in the data URL, look it up and force it
        if (params.url.indexOf('data:;') !== -1) {
          const mimeType = mime.lookup(file.name)
          if (!mimeType) {
            // If the mime type cannot be recognized, error
            this.setState({
              error: [
                // eslint-disable-next-line react/no-access-state-in-setstate
                ...this.state.error,
                `Unfortunately we can't process the file ${file.name}. Please try again with a different type of file.`,
              ],
            })
            return
          }
          params.url = params.url.replace('data:;', `data:${mimeType};`)
        }

        const { input, ...props } = this.props
        const value = input.value || props.value
        const handler = input.onChange || props.onChange
        if (handler) handler([...value, params])
      }

      reader.readAsDataURL(file)
    })
  }

  clearUpload = index => {
    // Clear errors, just in case
    this.setState({ error: [] })

    const { input, ...props } = this.props
    const value = input.value || props.value
    value.splice(index, 1)

    const handler = input.onChange || props.onChange
    if (handler) handler(value)
  }

  render() {
    const { meta, input, theme, label, ...props } = this.props
    const value = input.value || props.value
    const errored = (meta.touched && meta.error) || this.state.error.length > 0
    const errors = meta.touched ? [meta.error] : this.state.error

    return (
      <div className={cx(styles.wrapper, props.className)}>
        <label htmlFor={props.id} className={cx(styles.label, labelColor(theme))}>
          <Badge icon={ClipIcon} size="default" theme={theme} />
          <span>{label}</span>

          <input
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...input}
            id={props.id}
            type="file"
            value={undefined}
            className={styles.input}
            multiple
            onChange={this.handleUpload}
            onBlur={ev => ev.preventDefault()}
            aria-describedby={props.ariaDescribedby}
          />
        </label>
        {Boolean(value.length > 0) && (
          <div className={styles.attachments}>
            {value.map((file, index) => (
              <Attachment
                key={`file-${index}`}
                name={file.name}
                url={file.url}
                theme={theme}
                size="small"
                className={styles.file}
                onClear={() => this.clearUpload(index)}
              />
            ))}
          </div>
        )}
        {Boolean(errored) && (
          <div className={styles.errorWrapper}>
            {errors.map((error, index) => (
              <FormErrorMessage key={`error-${index}`} text={error} />
            ))}
          </div>
        )}
      </div>
    )
  }
}

MultiFileUpload.propTypes = {
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  input: PropTypes.shape({
    value: PropTypes.arrayOf(PropTypes.shape({})),
    onChange: PropTypes.func,
  }),
  value: PropTypes.arrayOf(PropTypes.shape({})),
  label: PropTypes.string,
  theme: PropTypes.oneOf(['', 'primary', 'secondary', 'success', 'warning', 'error', 'legacy', 'light', 'dark', 'tertiary', 'info']),
  className: PropTypes.string,
  ariaDescribedby: PropTypes.string,
}

MultiFileUpload.defaultProps = {
  meta: {},
  input: {},
  value: [],
  label: 'Attach files',
  theme: 'primary',
}
export default MultiFileUpload
